import { EnhancedDataTable } from '@/components/tables/enhanced-data-table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useCreateRequirement,
  useDeleteRequirement,
  useGetAllRequirements,
  useUpdateRequirement
} from '@/utils/api/membership-service'
import { AlertCircle, Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { RequirementDialog } from './components/RequirementDialog'
import { createColumns } from './components/columns'
import type { RequirementFormValues } from './schema'
import type { CategoryRequirement } from './types'
import { mapRequirementToApi, mapRequirementsResponse } from './utils'

export function RequirementsPage() {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingRequirement, setEditingRequirement] =
    useState<CategoryRequirement | undefined>()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [requirementToDelete, setRequirementToDelete] =
    useState<CategoryRequirement | undefined>()

  const requirementsQuery = useGetAllRequirements({
    query: {
      select: (response) => mapRequirementsResponse(response.data),
    },
  })

  const createRequirementMutation = useCreateRequirement({
    mutation: {
      onSuccess: () => {
        requirementsQuery.refetch()
        toast.success('Requirement created successfully')
      },
      onError: (error: unknown) => {
        const message =
          error instanceof Error ? error.message : 'Unexpected error occurred'
        toast.error(`Failed to create requirement: ${message}`)
      },
    },
  })

  const updateRequirementMutation = useUpdateRequirement({
    mutation: {
      onSuccess: () => {
        requirementsQuery.refetch()
        toast.success('Requirement updated successfully')
      },
      onError: (error: unknown) => {
        const message =
          error instanceof Error ? error.message : 'Unexpected error occurred'
        toast.error(`Failed to update requirement: ${message}`)
      },
    },
  })

  const deleteRequirementMutation = useDeleteRequirement({
    mutation: {
      onSuccess: () => {
        requirementsQuery.refetch()
        toast.success('Requirement deleted successfully')
      },
      onError: (error: unknown) => {
        const message =
          error instanceof Error ? error.message : 'Unexpected error occurred'
        toast.error(`Failed to delete requirement: ${message}`)
      },
    },
  })

  const handleCreateNew = () => {
    setEditingRequirement(undefined)
    setDialogOpen(true)
  }

  const handleEdit = (requirement: CategoryRequirement) => {
    setEditingRequirement(requirement)
    setDialogOpen(true)
  }

  const handleDelete = (requirement: CategoryRequirement) => {
    setRequirementToDelete(requirement)
    setDeleteDialogOpen(true)
  }

  const handleDialogClose = (open: boolean) => {
    setDialogOpen(open)
    if (!open) {
      setEditingRequirement(undefined)
    }
  }

  const handleSubmitDialog = async (values: RequirementFormValues) => {
    if (values.id) {
      await updateRequirementMutation.mutateAsync({
        id: values.id,
        data: mapRequirementToApi(values),
      })
    } else {
      await createRequirementMutation.mutateAsync({
        data: mapRequirementToApi(values),
      })
    }

    setDialogOpen(false)
    setEditingRequirement(undefined)
  }

  const confirmDelete = async () => {
    if (!requirementToDelete?.id) return

    await deleteRequirementMutation.mutateAsync({ id: requirementToDelete.id })

    setDeleteDialogOpen(false)
    setRequirementToDelete(undefined)
  }

  const columns = useMemo(
    () => createColumns(handleEdit, handleDelete),
    [handleEdit, handleDelete],
  )

  const isLoading = requirementsQuery.isLoading
  const isError = requirementsQuery.isError
  const error = requirementsQuery.error
  const requirements = requirementsQuery.data ?? []

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Membership Requirements</h1>
          <p className="text-muted-foreground">
            Manage reusable requirements that can be assigned to membership categories.
          </p>
        </div>
        <Button onClick={handleCreateNew}>
          <Plus className="mr-2 h-4 w-4" />
          New Requirement
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Requirements</CardTitle>
          <CardDescription>
            Create, edit, or remove requirements before attaching them to categories.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-[300px] w-full" />
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="h-12 w-12 text-destructive mb-4" />
              <h3 className="text-lg font-semibold mb-2">Error loading requirements</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {error instanceof Error ? error.message : 'Failed to load requirements'}
              </p>
              <Button onClick={() => requirementsQuery.refetch()}>
                Try Again
              </Button>
            </div>
          ) : requirements.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No requirements yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Create a requirement to start collecting additional information from applicants.
              </p>
              <Button onClick={handleCreateNew}>
                <Plus className="mr-2 h-4 w-4" />
                Create Requirement
              </Button>
            </div>
          ) : (
            <EnhancedDataTable
              data={requirements}
              columns={columns}
              searchPlaceholder="Search requirements..."
              enableGlobalFilter
              enableColumnFilters
              enableExport
              enableColumnVisibility
              enablePagination
              enableSorting
              enableRowSelection={false}
              pageSize={10}
              pageSizeOptions={[5, 10, 20, 30, 50]}
              loading={isLoading}
              emptyStateMessage="No requirements found. Create one to get started."
              exportOptions={{
                filename: 'membership-requirements',
                title: 'Membership Requirements Export',
              }}
              onRowClick={handleEdit}
            />
          )}
        </CardContent>
      </Card>

      <RequirementDialog
        open={dialogOpen}
        onOpenChange={handleDialogClose}
        requirement={editingRequirement}
        onSubmit={handleSubmitDialog}
        isSubmitting={
          createRequirementMutation.isPending || updateRequirementMutation.isPending
        }
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete requirement?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove
              {requirementToDelete?.label ? ` "${requirementToDelete.label}"` : ''}.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteRequirementMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteRequirementMutation.isPending}
            >
              {deleteRequirementMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
